import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Building2,
  Check,
  CreditCard,
  Users,
  Shield,
  FileText,
  Bell,
  BarChart3,
  Loader2,
  ArrowRight,
  Minus,
  Plus,
  Gift,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRICE_PER_UNIT_MONTHLY = 35;
const MINIMUM_MONTHLY_FEE = 750;
const MONTHS_PER_YEAR = 12;

export default function SignupPricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [unitCount, setUnitCount] = useState(10);
  const [step, setStep] = useState('pricing'); // pricing | details
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    building_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  
  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralValidated, setReferralValidated] = useState(null);
  const [validatingReferral, setValidatingReferral] = useState(false);

  // Check for referral code in URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode.toUpperCase());
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) {
      setReferralValidated(null);
      return;
    }
    
    setValidatingReferral(true);
    try {
      const response = await fetch(`${API_URL}/api/partner-program/validate-code/${code}`);
      const data = await response.json();
      setReferralValidated(data);
      if (data.valid) {
        toast.success(`Referral code applied: ${data.partner_name}`);
      }
    } catch (error) {
      console.error('Error validating referral:', error);
      setReferralValidated(null);
    } finally {
      setValidatingReferral(false);
    }
  };

  const calculatedMonthly = PRICE_PER_UNIT_MONTHLY * unitCount;
  const monthlyAmount = Math.max(calculatedMonthly, MINIMUM_MONTHLY_FEE);
  const annualAmount = monthlyAmount * MONTHS_PER_YEAR;
  const minimumApplied = calculatedMonthly < MINIMUM_MONTHLY_FEE;

  const features = [
    { icon: FileText, text: 'Levy billing & payment tracking' },
    { icon: Building2, text: 'Maintenance ticket management' },
    { icon: Users, text: 'Digital AGM voting' },
    { icon: Shield, text: 'Insurance claim management' },
    { icon: Bell, text: 'Real-time email notifications' },
    { icon: BarChart3, text: 'Automated trustee reports' },
  ];

  const handleProceedToDetails = () => {
    if (unitCount < 1) {
      toast.error('Please enter at least 1 unit');
      return;
    }
    setStep('details');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.building_name || !formData.contact_name || !formData.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          unit_count: unitCount,
          referral_code: referralValidated?.valid ? referralCode : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to initiate subscription');
      }

      const data = await response.json();
      
      if (data.success && data.gateway_url && data.payment_params) {
        // Create and submit form to PayFast
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.gateway_url;
        form.style.display = 'none';

        Object.entries(data.payment_params).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        });

        document.body.appendChild(form);
        toast.success('Redirecting to PayFast...');
        form.submit();
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to process subscription');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50">
      {/* Header */}
      <header className="p-4 sm:p-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div 
            className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0" 
            onClick={() => navigate('/')}
          >
            <img src="/logo.png" alt="BodyCorp365" className="w-8 h-8 sm:w-10 sm:h-10" />
            <span className="text-lg sm:text-xl font-bold text-slate-800 hidden sm:inline">BodyCorp365</span>
          </div>
          <Button variant="ghost" className="text-slate-700 text-sm sm:text-base whitespace-nowrap" onClick={() => navigate('/login')}>
            <span className="hidden sm:inline">Already have an account? </span>Log in
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {step === 'pricing' ? (
          <>
            {/* Hero */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Simple, Transparent Pricing
              </h1>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                R{PRICE_PER_UNIT_MONTHLY} per unit per month, 
                billed annually. Minimum R{MINIMUM_MONTHLY_FEE}/month. Everything included.
              </p>
            </div>

            {/* Pricing Calculator */}
            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Calculator Card */}
              <Card className="bg-white border-slate-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-slate-800 text-2xl">Calculate Your Price</CardTitle>
                  <CardDescription className="text-slate-600">
                    Enter the number of units in your building
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Number of Units</Label>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setUnitCount(Math.max(1, unitCount - 1))}
                        className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        value={unitCount}
                        onChange={(e) => setUnitCount(Math.max(1, parseInt(e.target.value) || 1))}
                        className="text-center text-2xl font-bold bg-white border-slate-300 text-slate-800 w-32"
                        min={1}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setUnitCount(unitCount + 1)}
                        className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600">Price per unit</span>
                      <span className="text-slate-800">R{PRICE_PER_UNIT_MONTHLY}/month</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600">Units</span>
                      <span className="text-slate-800">× {unitCount}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600">Calculated monthly</span>
                      <span className={minimumApplied ? "text-slate-400 line-through" : "text-slate-800"}>
                        R{calculatedMonthly.toLocaleString()}
                      </span>
                    </div>
                    {minimumApplied && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-amber-600 text-sm">Minimum monthly fee</span>
                        <span className="text-amber-600 font-medium">R{MINIMUM_MONTHLY_FEE.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-slate-700 font-medium">Monthly total</span>
                      <span className="text-slate-900 font-semibold">R{monthlyAmount.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-emerald-200 pt-4">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-slate-500">Billed annually (12 months)</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-slate-800">Annual Total</span>
                        <span className="text-3xl font-bold text-emerald-600">
                          R{annualAmount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Referral Code Input */}
                  <div className="mb-4">
                    <Label className="text-slate-600 text-sm">Have a referral code?</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="Enter code (e.g., ABC-1234)"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
                        data-testid="referral-code-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => validateReferralCode(referralCode)}
                        disabled={validatingReferral || !referralCode}
                        className="border-slate-300 text-slate-700 hover:bg-slate-50"
                        data-testid="validate-referral-btn"
                      >
                        {validatingReferral ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    {referralValidated && (
                      <div className={`mt-2 p-2 rounded text-sm ${
                        referralValidated.valid 
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {referralValidated.valid ? (
                          <div className="flex items-center gap-2">
                            <Gift className="w-4 h-4" />
                            <span>Referred by: {referralValidated.partner_name}</span>
                          </div>
                        ) : (
                          <span>{referralValidated.message}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <Button 
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg shadow-md"
                    onClick={handleProceedToDetails}
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Features Card */}
              <Card className="bg-white border-slate-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-slate-800 text-2xl">Everything Included</CardTitle>
                  <CardDescription className="text-slate-600">
                    All features, all units, one price
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <feature.icon className="w-4 h-4 text-emerald-600" />
                        </div>
                        <span className="text-slate-700">{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 p-4 bg-sky-50 rounded-lg border border-sky-100">
                    <h4 className="font-semibold text-slate-800 mb-2">Plus:</h4>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-500" /> Contractor marketplace
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-500" /> Document management
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-500" /> Trustee communication hub
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-400" /> Mobile-friendly access
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-400" /> Unlimited users
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          /* Details Form */
          <div className="max-w-xl mx-auto">
            <Button 
              variant="ghost" 
              className="text-slate-600 hover:text-slate-800 mb-6"
              onClick={() => setStep('pricing')}
            >
              ← Back to pricing
            </Button>

            <Card className="bg-white border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-slate-800 text-2xl">Complete Your Signup</CardTitle>
                <CardDescription className="text-slate-600">
                  {unitCount} units × R{PRICE_PER_UNIT_MONTHLY}/month × 12 months = <span className="text-emerald-600 font-semibold">R{annualAmount.toLocaleString()}/year</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-slate-700">Building/Estate Name *</Label>
                    <Input
                      value={formData.building_name}
                      onChange={(e) => setFormData({ ...formData, building_name: e.target.value })}
                      placeholder="e.g., Sunset Heights Estate"
                      className="mt-1 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">Contact Name *</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="Your full name"
                      className="mt-1 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">Email Address *</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="you@example.com"
                      className="mt-1 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">Phone Number</Label>
                    <Input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+27 12 345 6789"
                      className="mt-1 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
                    />
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit"
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg shadow-md"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Pay R{annualAmount.toLocaleString()} with PayFast
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-slate-500 text-center">
                    You'll be redirected to PayFast's secure payment page
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="p-6 text-center text-slate-500 text-sm">
        <p>© 2026 BodyCorp365. All rights reserved.</p>
      </footer>
    </div>
  );
}
